'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import ServicesTabs, { ServiceCategory, Platform } from './ServicesTabs';
import PricingCards, { PricingTier } from './PricingCards';
import OrderForm from './OrderForm';
import Icon from '@/components/ui/AppIcon';

const categoryToRoute: Record<ServiceCategory, string> = {
  followers: '/facebook-followers',
  likes: '/facebook-likes',
  comments: '/facebook-comments',
  views: '/facebook-views',
  tiktok_followers: '/tiktok-followers',
  tiktok_likes: '/tiktok-likes',
  tiktok_views: '/tiktok-views',
  tiktok_comments: '/tiktok-comments',
  instagram_followers: '/instagram-followers',
  instagram_likes: '/instagram-likes',
  instagram_views: '/instagram-views',
  instagram_comments: '/instagram-comments',
  youtube_followers: '/youtube-followers',
  youtube_likes: '/youtube-likes',
  youtube_views: '/youtube-views',
  youtube_comments: '/youtube-comments',
};

export default function ServicesInteractive() {
  const router = useRouter();
  const [platform, setPlatform] = useState<Platform>('facebook');
  const [activeCategory, setActiveCategory] = useState<ServiceCategory>('followers');
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);

  const handlePlatformChange = (p: Platform) => {
    setPlatform(p);
    setSelectedTier(null);
    // Reset active category display for the new platform — no redirect
    if (p === 'facebook') setActiveCategory('followers');
    else if (p === 'tiktok') setActiveCategory('tiktok_followers');
    else if (p === 'instagram') setActiveCategory('instagram_followers');
    else if (p === 'youtube') setActiveCategory('youtube_followers');
  };

  const handleCategoryChange = (cat: ServiceCategory) => {
    // Navigate to the dedicated sub-page when user explicitly selects a category
    router.push(categoryToRoute[cat]);
  };

  const handleTierSelect = (tier: PricingTier) => {
    setSelectedTier(tier);
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  };

  const isTiktok = platform === 'tiktok';
  const isInstagram = platform === 'instagram';
  const isYoutube = platform === 'youtube';

  const categoryInfo: Record<ServiceCategory, { title: string; desc: string; emoji: string; color: string }> = {
    followers: {
      title: 'Abonnés Facebook',
      desc: 'Augmentez votre audience avec des abonnés réels et actifs. Livraison progressive et naturelle.',
      emoji: '👥',
      color: '#1877F2',
    },
    likes: {
      title: 'Likes & Réactions',
      desc: 'Boostez l\'engagement de vos publications avec des likes et réactions authentiques.',
      emoji: '👍',
      color: '#F4A923',
    },
    comments: {
      title: 'Commentaires Facebook',
      desc: 'Des commentaires en français personnalisés pour rendre votre contenu plus crédible.',
      emoji: '💬',
      color: '#10B981',
    },
    views: {
      title: 'Vues Facebook',
      desc: 'Augmentez le nombre de vues sur vos vidéos et publications Facebook.',
      emoji: '👁️',
      color: '#8B5CF6',
    },
    tiktok_followers: {
      title: 'Abonnés TikTok',
      desc: 'Développez votre communauté TikTok avec des abonnés réels et actifs.',
      emoji: '👥',
      color: '#2563EB',
    },
    tiktok_likes: {
      title: 'Likes TikTok',
      desc: 'Boostez l\'engagement de vos vidéos TikTok avec des likes authentiques.',
      emoji: '❤️',
      color: '#2563EB',
    },
    tiktok_views: {
      title: 'Vues TikTok',
      desc: 'Augmentez le nombre de vues sur vos vidéos TikTok rapidement.',
      emoji: '▶️',
      color: '#2563EB',
    },
    tiktok_comments: {
      title: 'Commentaires TikTok',
      desc: 'Des commentaires personnalisés pour rendre vos vidéos TikTok plus crédibles.',
      emoji: '💬',
      color: '#2563EB',
    },
    instagram_followers: {
      title: 'Abonnés Instagram',
      desc: 'Développez votre communauté Instagram avec des abonnés réels et actifs. Croissance naturelle garantie.',
      emoji: '📸',
      color: '#C13584',
    },
    instagram_likes: {
      title: 'Likes Instagram',
      desc: 'Boostez l\'engagement de vos publications Instagram avec des likes authentiques et rapides.',
      emoji: '❤️',
      color: '#C13584',
    },
    instagram_comments: {
      title: 'Commentaires Instagram',
      desc: 'Des commentaires personnalisés en français pour rendre vos publications Instagram plus crédibles.',
      emoji: '💬',
      color: '#C13584',
    },
    instagram_views: {
      title: 'Vues Instagram',
      desc: 'Augmentez le nombre de vues sur vos Reels et vidéos Instagram rapidement.',
      emoji: '▶️',
      color: '#C13584',
    },
    youtube_followers: {
      title: 'Abonnés YouTube',
      desc: 'Développez votre communauté YouTube avec des abonnés réels et actifs.',
      emoji: '👥',
      color: '#FF0000',
    },
    youtube_likes: {
      title: 'Likes YouTube',
      desc: 'Boostez l\'engagement de vos vidéos YouTube avec des likes authentiques.',
      emoji: '👍',
      color: '#FF0000',
    },
    youtube_views: {
      title: 'Vues YouTube',
      desc: 'Augmentez le nombre de vues sur vos vidéos YouTube rapidement.',
      emoji: '▶️',
      color: '#FF0000',
    },
    youtube_comments: {
      title: 'Commentaires YouTube',
      desc: 'Des commentaires personnalisés pour rendre vos vidéos YouTube plus crédibles.',
      emoji: '💬',
      color: '#FF0000',
    },
  };

  const info = categoryInfo[activeCategory];

  const sectionBg = isTiktok
    ? 'linear-gradient(135deg,#0a0a0a 0%,#0a0a1a 100%)'
    : isInstagram
    ? 'linear-gradient(135deg,#1a0a1a 0%,#0d0a1a 100%)'
    : isYoutube
    ? '#F4F6FB' :'#F4F6FB';

  return (
    <section className="py-16 px-4 sm:px-6" style={{ background: sectionBg }}>
      <div className="max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex flex-col items-center gap-5 mb-10">
          <ServicesTabs
            active={activeCategory}
            onChange={handleCategoryChange}
            platform={platform}
            onPlatformChange={handlePlatformChange}
          />

          {/* Category info bar */}
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-2xl w-full max-w-2xl"
            style={{
              background: isTiktok || isInstagram ? 'rgba(255,255,255,0.05)' : 'white',
              border: `1.5px solid ${info.color}33`,
              boxShadow: '0 2px 16px rgba(13,27,62,0.05)',
            }}
          >
            <span className="text-2xl">{info.emoji}</span>
            <div>
              <div className="font-heading font-bold text-sm" style={{ color: isTiktok || isInstagram ? 'white' : '#0D1B3E' }}>{info.title}</div>
              <div className="text-xs" style={{ color: isTiktok || isInstagram ? 'rgba(255,255,255,0.6)' : '#6B7280' }}>{info.desc}</div>
            </div>
            {selectedTier && (
              <div className="ml-auto flex items-center gap-1.5 text-xs font-bold" style={{ color: info.color }}>
                <Icon name="CheckCircleIcon" size={14} variant="solid" style={{ color: info.color }} />
                {selectedTier.qty} sélectionné
              </div>
            )}
          </div>

          {/* TikTok brand badge */}
          {isTiktok && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#010101,#2563EB)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}
            >
              🎵 Services TikTok — Croissance Authentique
            </div>
          )}

          {/* Instagram brand badge */}
          {isInstagram && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#833AB4,#FD1D1D,#F77737)', boxShadow: '0 4px 16px rgba(193,53,132,0.35)' }}
            >
              📸 Services Instagram — Croissance Authentique
            </div>
          )}

          {/* YouTube brand badge */}
          {isYoutube && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#CC0000,#FF0000)', boxShadow: '0 4px 16px rgba(255,0,0,0.35)' }}
            >
              ▶️ Services YouTube — Croissance Authentique
            </div>
          )}
        </div>

        {/* Main grid: pricing + order form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pricing cards — 2/3 width */}
          <div className="lg:col-span-2">
            <PricingCards
              category={activeCategory}
              onSelect={handleTierSelect}
              selectedId={selectedTier?.id ?? null}
              isTiktok={isTiktok}
              isInstagram={isInstagram}
              isYoutube={isYoutube}
            />

            {/* Features comparison */}
            <div
              className="mt-6 p-6 rounded-3xl"
              style={{
                background: isTiktok || isInstagram ? 'rgba(255,255,255,0.05)' : 'white',
                border: isTiktok
                  ? '1px solid rgba(37,99,235,0.2)'
                  : isInstagram
                  ? '1px solid rgba(193,53,132,0.2)'
                  : isYoutube
                  ? '1px solid rgba(255,0,0,0.15)'
                  : '1px solid rgba(13,27,62,0.06)',
              }}
            >
              <h4 className="font-heading font-bold text-sm mb-4 flex items-center gap-2" style={{ color: isTiktok || isInstagram ? 'white' : '#0D1B3E' }}>
                <Icon name="ShieldCheckIcon" size={16} className="text-[#10B981]" />
                Tous nos packs incluent
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { icon: '✅', text: 'Profils réels & actifs' },
                  { icon: '🔒', text: 'Sans mot de passe' },
                  { icon: '⚡', text: 'Livraison automatique' },
                  { icon: '🛡️', text: 'Garantie 30 jours' },
                  { icon: '💬', text: 'Support WhatsApp' },
                  { icon: '🔄', text: 'Remplacement gratuit' },
                ].map((f) => (
                  <div
                    key={f.text}
                    className="flex items-center gap-2 p-3 rounded-xl text-xs font-medium"
                    style={{
                      background: isTiktok || isInstagram ? 'rgba(255,255,255,0.06)' : 'rgba(244,246,251,0.8)',
                      color: isTiktok || isInstagram ? 'rgba(255,255,255,0.8)' : '#374151',
                    }}
                  >
                    <span>{f.icon}</span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order form — 1/3 width, sticky */}
          <div className="lg:sticky lg:top-24 h-fit">
            <OrderForm selectedTier={selectedTier} category={activeCategory} isTiktok={isTiktok} isInstagram={isInstagram} isYoutube={isYoutube} />
          </div>
        </div>
      </div>
    </section>
  );
}